import { ref } from 'vue';
import { THEME_SKINS, type ThemeSkin } from '@s-blog/shared-types';

/**
 * 整站视觉主题皮肤 (模块级单例)。两层模型 (复刻 useBgPreference):
 *   localStorage 有值 → 访客覆盖优先; 无值 → 站长默认 (applyOwnerDefault, 来自 SiteConfig); 都没 → 'aurora'。
 * **持久化只在访客显式 setThemeSkin 时写 localStorage** —— 不在别处自动落盘,
 * 否则首屏自动落盘会让"没动过设置的访客"永远吃不到站长默认。
 *
 * 应用机制: documentElement.dataset.theme → global.css 的 :root[data-theme="polar"] 覆盖 --aurora-* 变量块。
 *   aurora = 移除属性回到 :root 默认 (零侵入); polar = 挂 data-theme="polar"。
 */
export type { ThemeSkin };

const STORAGE_KEY = 'aurora-theme-skin';
const isValid = (v: string | null): v is ThemeSkin =>
  !!v && (THEME_SKINS as readonly string[]).includes(v);

const stored: ThemeSkin | null = (() => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return isValid(s) ? s : null;
  } catch {
    return null;
  }
})();
const hadStored = stored !== null;
const themeSkin = ref<ThemeSkin>(stored ?? 'aurora');

function apply(skin: ThemeSkin) {
  const el = document.documentElement;
  if (skin === 'aurora') delete el.dataset.theme;
  else el.dataset.theme = skin;
}
// 模块加载即应用 (在 App mount 前), 避免首屏闪烁 (FOUC)
apply(themeSkin.value);

function persist(v: ThemeSkin) {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // ignore (privacy mode)
  }
}

export function useThemeSkin() {
  function setThemeSkin(v: ThemeSkin) {
    themeSkin.value = v;
    apply(v);
    persist(v); // 访客显式选择 → 落盘 (从此覆盖站长默认)
  }
  /** 站长默认 (SiteConfig.defaultThemeSkin): 仅当访客从没手动设过时套用, 不落盘 (站长改了默认还能再生效) */
  function applyOwnerDefault(v: ThemeSkin | null | undefined) {
    if (!hadStored && isValid(v ?? null)) {
      themeSkin.value = v as ThemeSkin;
      apply(v as ThemeSkin);
    }
  }
  return { themeSkin, setThemeSkin, applyOwnerDefault };
}
